import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Mailbox, ArrowRight, Check, Zap, Rocket, Crown, ShieldCheck, Repeat, FolderTree, Gauge } from "lucide-react";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/cd8e3a71-ec3e-4a39-bf67-778ba7cb11b8/images/a9c5042425f93cafea56ddac062e2f602ee99631882750de86a39ef5120547cc.jpeg";
const ICONS = [Zap, Rocket, Crown];

const FEATURES = [
  { icon: Gauge, title: "Real-time progress", desc: "Live progress bars, folder & email counts, and a streaming log for every migration." },
  { icon: FolderTree, title: "Smart folder mapping", desc: "Inbox → Inbox, Sent → Sent. No messy Gmail labels or duplicate tags." },
  { icon: Repeat, title: "Recurring sync", desc: "Keep the new mailbox topped up — re-sync on your own schedule, only new mail." },
  { icon: ShieldCheck, title: "Secure by design", desc: "Encrypted credentials, 2FA and brute-force protection built in." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [packages, setPackages] = useState([]);
  const name = settings?.site_name || "MailShift";

  useEffect(() => { api.get("/packages").then((r) => setPackages(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-border bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            {settings?.logo_url ? <img src={settings.logo_url} alt={name} className="h-9 w-9 rounded-md object-cover" />
              : <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary text-white"><Mailbox className="h-5 w-5" /></div>}
            <span className="font-heading text-lg font-bold tracking-tighter">{name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-button">Sign in</Button>
            <Button className="gap-2" onClick={() => navigate("/register")} data-testid="landing-getstarted-button">
              Get started <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-24">
          <div>
            <span className="inline-block rounded-full border border-primary/30 bg-accent px-3 py-1 text-xs font-medium text-primary">IMAP → IMAP migration</span>
            <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
              {settings?.hero_title || "Migrate mailboxes directly, provider to provider"}
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground sm:text-lg">
              {settings?.hero_subtitle || "Bulk IMAP migrations with real-time progress, folder mapping and live logs."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="gap-2" onClick={() => navigate("/register")} data-testid="hero-cta-button">
                Start migrating <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
                View pricing
              </Button>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">Gmail · Outlook · Office 365 · Zoho · cPanel · any IMAP</p>
          </div>
          <div className="relative">
            <img src={HERO} alt="Email migration" className="h-80 w-full rounded-xl object-cover shadow-2xl md:h-[420px]" />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-secondary/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <h2 className="font-heading text-3xl font-bold tracking-tighter">Everything you need to move email</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1">
              <f.icon className="h-7 w-7 text-primary" />
              <h3 className="mt-4 font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-border bg-muted/30 py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tighter sm:text-4xl">{settings?.pricing_title || "Simple, transparent pricing"}</h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">{settings?.pricing_subtitle || "Choose a plan that fits your migration needs."}</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {packages.map((p, i) => {
              const Icon = ICONS[i % ICONS.length];
              const highlight = i === 1;
              return (
                <div key={p.id} className={`relative flex flex-col border bg-card p-8 ${highlight ? "border-primary shadow-lg shadow-primary/10" : "border-border"}`}>
                  {highlight && <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">Popular</span>}
                  <Icon className="h-8 w-8 text-primary" />
                  <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight">{p.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-heading text-4xl font-bold tracking-tighter">₹{(p.price / 100).toLocaleString("en-IN")}</span>
                    <span className="mb-1 text-sm text-muted-foreground">/{p.validity_days} days</span>
                  </div>
                  <ul className="mt-6 flex-1 space-y-3 text-sm">
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{p.max_migrations === -1 ? "Unlimited migrations" : `${p.max_migrations} migrations`}</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />Up to {p.storage_mb != null ? (p.storage_mb >= 1024 ? `${(p.storage_mb/1024).toFixed(p.storage_mb % 1024 === 0 ? 0 : 1)}GB` : `${p.storage_mb}MB`) : `${p.storage_per_mailbox_gb}GB`} per mailbox</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{p.max_mailboxes === -1 ? "Unlimited mailboxes" : `${p.max_mailboxes} mailboxes`}</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />Recurring sync & scheduling</li>
                  </ul>
                  <Button className="mt-8 w-full gap-2" onClick={() => navigate("/register")} data-testid={`landing-buy-${p.id}`}>Get started</Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="mx-auto max-w-6xl px-5 text-center text-sm text-muted-foreground">
          © {name}. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
